import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { ApiService } from '../services/api';
import toast from 'react-hot-toast';

interface PendingChannel {
    id: number;
    channel_name: string;
    youtube_url: string;
    language: string | null;
    active: number | null;
    full_scan: number | null;
    status: string;
    video_count?: number | null;
    last_upload?: string | null;
    checked_at?: string | null;
}

const LANGUAGE_OPTIONS = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'tl', name: 'Filipino/Tagalog' },
    { code: 'th', name: 'Thai' },
    { code: 'ko', name: 'Korean' },
    { code: 'ja', name: 'Japanese' },
    { code: 'zh', name: 'Chinese' },
    { code: 'hi', name: 'Hindi' },
    { code: 'ar', name: 'Arabic' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'ru', name: 'Russian' },
    { code: 'tr', name: 'Turkish' },
    { code: 'vi', name: 'Vietnamese' },
    { code: 'id', name: 'Indonesian' },
    { code: 'ms', name: 'Malay' },
    { code: 'bn', name: 'Bengali' }
];

const AdminPendingChannelsPage: React.FC = () => {
    const { user } = useAuth();
    const [pendingChannels, setPendingChannels] = useState<PendingChannel[]>([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchPendingChannels();
    }, []);

    const fetchPendingChannels = async () => {
        try {
            setLoading(true);
            const response = await ApiService.getPendingChannels();
            setPendingChannels(response);
        } catch (err) {
            setError('Failed to fetch pending channels');
            console.error('Error fetching pending channels:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (channelId: number, channelName: string, language: string | null) => {
        try {
            setUpdating(channelId);
            await ApiService.approveChannel(channelId, {
                channel_name: channelName,
                language: language || 'en',
                active: 1,
                full_scan: 1,
                status: 'verified'
            });

            // Remove from the list
            setPendingChannels(prev => prev.filter(channel => channel.id !== channelId));

            // Show success toast
            toast.success(`✅ Channel "${channelName}" approved successfully!`);
        } catch (err) {
            setError('Failed to approve channel');
            console.error('Error approving channel:', err);
            toast.error('❌ Failed to approve channel');
        } finally {
            setUpdating(null);
        }
    };

    const handleReject = async (channelId: number) => {
        try {
            setUpdating(channelId);
            await ApiService.rejectChannel(channelId, {
                full_scan: 0,
                status: 'verified'
            });

            // Remove from the list
            setPendingChannels(prev => prev.filter(channel => channel.id !== channelId));

            // Show success toast
            toast.success('✅ Channel rejected successfully!');
        } catch (err) {
            setError('Failed to reject channel');
            console.error('Error rejecting channel:', err);
            toast.error('❌ Failed to reject channel');
        } finally {
            setUpdating(null);
        }
    };

    const handleChannelNameChange = (channelId: number, newName: string) => {
        setPendingChannels(prev =>
            prev.map(channel =>
                channel.id === channelId
                    ? { ...channel, channel_name: newName }
                    : channel
            )
        );
    };

    const handleLanguageChange = (channelId: number, newLanguage: string) => {
        setPendingChannels(prev =>
            prev.map(channel =>
                channel.id === channelId
                    ? { ...channel, language: newLanguage }
                    : channel
            )
        );
    };

    const openChannelUrl = (url: string) => {
        window.open(url, '_blank');
    };

    if (!user?.is_admin) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
                    <p className="text-gray-600">You need admin privileges to access this page.</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading pending channels...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-4">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-4">
                    <h1 className="text-2xl font-bold text-gray-900">Pending Channels Review</h1>
                    <p className="mt-1 text-gray-600">
                        Review and approve/reject channels from the pending list
                    </p>
                </div>

                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-800">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {pendingChannels.length === 0 ? (
                    <div className="text-center py-12">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No pending channels</h3>
                        <p className="mt-1 text-sm text-gray-500">All channels have been reviewed.</p>
                    </div>
                ) : (
                    <div className="bg-white shadow overflow-hidden rounded-md">
                        <ul className="divide-y divide-gray-200">
                            {pendingChannels.map((channel) => (
                                <li key={channel.id} className="px-6 py-4">
                                    <div className="flex items-start space-x-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center space-x-2 mb-2">
                                                <input
                                                    type="text"
                                                    value={channel.channel_name}
                                                    onChange={(e) => handleChannelNameChange(channel.id, e.target.value)}
                                                    className="text-lg font-medium text-gray-900 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                                <select
                                                    value={channel.language || 'en'}
                                                    onChange={(e) => handleLanguageChange(channel.id, e.target.value)}
                                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                >
                                                    {LANGUAGE_OPTIONS.map(option => (
                                                        <option key={option.code} value={option.code}>
                                                            {option.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="flex items-center space-x-4 text-sm text-gray-500 mb-2">
                                                <button
                                                    onClick={() => openChannelUrl(channel.youtube_url)}
                                                    className="text-blue-600 hover:text-blue-800 underline"
                                                >
                                                    {channel.youtube_url}
                                                </button>
                                            </div>

                                            <div className="flex items-center space-x-4 text-xs text-gray-400">
                                                {channel.video_count !== null && channel.video_count !== undefined && (
                                                    <span>Videos: {channel.video_count}</span>
                                                )}
                                                {channel.last_upload && (
                                                    <span>Last Upload: {new Date(channel.last_upload).toLocaleDateString()}</span>
                                                )}
                                                <span>Status: {channel.status}</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-2 flex-shrink-0">
                                            <button
                                                onClick={() => handleApprove(channel.id, channel.channel_name, channel.language)}
                                                disabled={updating === channel.id}
                                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {updating === channel.id ? (
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                ) : (
                                                    'Approve'
                                                )}
                                            </button>

                                            <button
                                                onClick={() => handleReject(channel.id)}
                                                disabled={updating === channel.id}
                                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {updating === channel.id ? (
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                ) : (
                                                    'Reject'
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPendingChannelsPage; 